"use client"

import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import Image from "next/image"
import { useEffect, useState } from "react"
import { CustomBreadcrumbs } from "ui"
import Container from "ui/Container"
import {
  extractProperty,
  getBreadCrumbs,
  getNonUmbracoUrl,
  Link,
  transformProperties,
} from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

const StyledDiv = styled.div`
  width: 100%;
`

const StyledBreadcrumbsContainer = styled.div`
  padding: 0 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 0;
  }
`

const StyledContentDiv = styled.div`
  padding-top: 32px;
  padding-bottom: 24px;
  h3 {
    ${({ theme }) => theme.breakpoints.up("md")} {
      padding-bottom: 48px;
    }
  }
`

const StyledTabsDiv = styled.div`
  position: sticky;
  top: 64px;
  z-index: 100;
  background-color: white;
  ${({ theme }) => theme.breakpoints.up("md")} {
    top: 80px;
  }
`

const StyledTabContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-wrap: wrap;

  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const StyledTabButton = styled(Button)<{ isActive: boolean }>`
  padding: 16px 5px 16px 5px;
  min-width: 70px;
  min-height: 48px;
  height: auto;

  ${({ theme }) => theme.breakpoints.up("md")} {
    min-width: 159px;
    padding: 16px;
  }

  font-size: 20px;
  font-weight: 400;
  border: 1px solid #d1d5db;
  background-color: ${(p) => (p.isActive ? "#008761" : "white")};
  color: ${(p) => (p.isActive ? "white" : "#051711")};
`

const StyledInnerContentDiv = styled.div`
  padding-top: 32px;
  padding-bottom: 48px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 48px;
    padding-bottom: 64px;
  }
`

const StyeledItem = styled.div`
  padding-top: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #d1d5db;
  ${({ theme }) => theme.breakpoints.up("md")} {
    border-top: 1px solid #d1d5db;
    border-bottom: none;
    padding-top: 32px;
    padding-bottom: 32px;
  }
`
const StyeledItemMain = styled.div`
  padding-top: 32px;
  ${({ theme }) => theme.breakpoints.down("md")} {
    > div:last-child {
      border-bottom: none;
    }
    > div:first-child {
      padding-top: 0px;
    }
  }
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 48px;
    > div:last-child {
      border-bottom: 1px solid #d1d5db;
    }
  }
`

const StyledOpeningHour = styled.div`
  width: 300px;
  flex-shrink: 0;
  ${({ theme }) => theme.breakpoints.down("md")} {
    width: 100%;
  }
`

const StyledOpeningHourWrapper = styled.div`
  display: flex;
`

const StyledRowdiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 80px;
    align-items: flex-start;
  }
`

const StyledLocationDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  a {
    text-decoration: none;
  }
`

const StyledLocAdressWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledAccessibleDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`

const StyledMainDiv = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme }) => theme.breakpoints.down("md")} {
    gap: 24px;
    padding-top: 24px;
  }
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    justify-content: space-between;
  }
`
type Props = {
  data: any
  locale: string
}

const KavStations = ({ data, locale }: Props) => {
  const [activeTab, setActiveTab] = useState<string>("")
  const parsedData = data?.page?.properties || []
  const { blocks } = transformProperties(parsedData)
  const breadCrumbs = getBreadCrumbs(data, locale)

  const ravKavServiceStation = blocks?.blocks?.find(
    (block: any) => block.blockType === "ravKavServiceStation",
  )
  const title = extractProperty(ravKavServiceStation, "title")
  const itemsList = extractProperty(ravKavServiceStation, "itemsList")

  const [selectedData, setSelectedData] = useState<any>([])

  useEffect(() => {
    if (typeof window !== "undefined") {
      // Remove the '#' character from the hash before setting it.
      const currentHash = window.location.hash.replace("#", "")
      const filtered = currentHash
        ? itemsList.filter(
            (item: any) => extractProperty(item, "encorID") === currentHash,
          )
        : [itemsList?.[0]]
      const tab = extractProperty(filtered[0], "tab")
      setActiveTab(tab)

      const filteredItemsList = extractProperty(filtered[0], "itemsList")
      setSelectedData(filteredItemsList)
    }
  }, [])

  const HandleButtonClick = (tab: string) => {
    setActiveTab(tab)
    const filtered = itemsList.filter(
      (item: any) => extractProperty(item, "tab") === tab,
    )
    const filteredItemsList = extractProperty(filtered[0], "itemsList")
    const encorID = extractProperty(filtered[0], "encorID") || ""
    setSelectedData(filteredItemsList)

    if (typeof window !== "undefined") {
      // Replace the current hash in the URL with the new encorID.
      window.history.replaceState(null, "", `#${encorID}`)
      // Alternatively, you can use:
      // window.location.hash = encorID;
    }
  }
  return (
    <StyledDiv>
      <Container paddingMobile>
        <StyledBreadcrumbsContainer>
          <CustomBreadcrumbs breadcrumbs={breadCrumbs} />
        </StyledBreadcrumbsContainer>
        <StyledContentDiv>
          <Typography variant="h3">{title}</Typography>
        </StyledContentDiv>
      </Container>
      <StyledTabsDiv>
        <Container paddingMobile>
          <StyledTabContainer>
            {itemsList?.map((item: any, index: number) => {
              const tab = extractProperty(item, "tab")
              return (
                <StyledTabButton
                  key={index}
                  isActive={tab === activeTab}
                  onClick={() => HandleButtonClick(tab)}
                >
                  {tab}
                </StyledTabButton>
              )
            })}
          </StyledTabContainer>
        </Container>
      </StyledTabsDiv>
      <Container paddingMobile>
        <StyledInnerContentDiv>
          <Typography variant="body2">
            מצאנו <span style={{ fontWeight: 700 }}>{selectedData.length}</span>{" "}
            עמדות רב-קו
          </Typography>
          <StyeledItemMain>
            {selectedData &&
              selectedData?.map((item: any, index: number) => {
                const title = extractProperty(item, "title")
                const itemsList = extractProperty(item, "itemsList")
                const locationTitle = extractProperty(item, "locationTitle")
                const loacationIcon = extractProperty(item, "loacationIcon")
                const address = extractProperty(item, "address")
                const accessibleText = extractProperty(item, "accessibleText")
                const accessibleIcon = extractProperty(item, "accessibleIcon")
                const addressLink = extractProperty(item, "addressLink")
                return (
                  <StyeledItem key={index}>
                    <Typography variant="subtitle1">{title}</Typography>
                    <StyledMainDiv>
                      <StyledRowdiv>
                        <StyledOpeningHour>
                          {itemsList.map((item: any, index: number) => {
                            const day = extractProperty(item, "day")
                            const hour = extractProperty(item, "hour")
                            return (
                              <StyledOpeningHourWrapper key={index}>
                                <Typography
                                  variant="body2"
                                  sx={{ flexShrink: 0 }}
                                >
                                  {day}
                                </Typography>
                                &nbsp;
                                <Typography
                                  variant="body2"
                                  sx={{ flexShrink: 0 }}
                                >
                                  {hour}
                                </Typography>
                              </StyledOpeningHourWrapper>
                            )
                          })}
                        </StyledOpeningHour>
                        <StyledLocAdressWrapper>
                          <StyledLocationDiv>
                            <Image
                              src={getUmbracoMediaUrl(loacationIcon)}
                              width={20}
                              height={20}
                              alt="location icon"
                            />
                            <Link
                              href={getNonUmbracoUrl(addressLink?.[0]?.url)}
                              target={addressLink?.[0]?.target}
                            >
                              <Typography
                                variant="body1"
                                style={{ fontWeight: 700 }}
                              >
                                {locationTitle}
                              </Typography>
                            </Link>
                          </StyledLocationDiv>
                          <Typography
                            variant="body2"
                            style={{ textDecoration: "underline" }}
                          >
                            {address}
                          </Typography>
                        </StyledLocAdressWrapper>
                      </StyledRowdiv>
                      <StyledAccessibleDiv>
                        <Image
                          src={getUmbracoMediaUrl(accessibleIcon)}
                          width={20}
                          height={20}
                          alt="accessible"
                        />
                        <Typography variant="body2">
                          {accessibleText}
                        </Typography>
                      </StyledAccessibleDiv>
                    </StyledMainDiv>
                  </StyeledItem>
                )
              })}
          </StyeledItemMain>
        </StyledInnerContentDiv>
      </Container>
    </StyledDiv>
  )
}

export default KavStations
